import { defaultFileFilters } from "constants/file";
import { defaultMeasureDataTableFilters } from "constants/measure-data";
import { staticReportsQueries } from "constants/reports";
import { createContext, useContextSelector } from "use-context-selector";
import { correlationMatrixFromURL } from "utils/correlation-matrix-from-URL";
import DealsStateFromQueries from "utils/deals-state-from-queries";
import { quickViewQueriesFromURL } from "utils/quick-view-queries-from-URL";
import { reportStateFromQueries } from "utils/reports-state-from-queries";
import viewStateFromQueries from "utils/view-state-from-queries";

import {
  IBulkUploadState,
  IComments,
  IDealModule,
  IReports,
  ITagger,
  IUserModule,
  IView,
  TaskManagment
} from "./types";
import { DataRooms } from "./types/data-rooms";
import { Document } from "./types/documents";

const reportsQueries = reportStateFromQueries(location.search);
const quickViewQueries = quickViewQueriesFromURL(location.search);
const correlationMatrixSettings = correlationMatrixFromURL(location.search);

export interface IStore {
  userModule: IUserModule;
  dealModule: IDealModule;
  comments: IComments;
  // notifications: INotifications;
  view: IView;
  reports: IReports;
  taskManagment: TaskManagment;
  documents: Document[];
  dataRooms: DataRooms;
  tagger: ITagger;
  bulkUpload: IBulkUploadState;
}

export const userModule: IUserModule = {
  user: {
    id: null!,
    first_name: "",
    last_name: "",
    full_name: "",
    company_name: "",
    account_id: null!,
    phone_number: "",
    enable_2fa: false,
    email_notification: null!,
    email: "",
    email_verified_at: "",
    profile_photo_path: "",
    profile_photo_url: "",
    role: null!,
    role_id: null!,
    blank_template_id: null!,
    created_at: "",
    password_reminder: false,
    password_expired: false,
    has_only_external_accounts: false,
    has_shared_global_documents: null,
    has_shared_deals: null,
    last_seen: null
  },
  token: "",
  broadcast_token: "",
  account: {
    id: null!,
    name: "",
    domain: "",
    api_domain: "",
    under_maintenance: null!,
    logo_url: "",
    is_report_enabled: false,
    allow_dataroom: false,
    allow_import_deal_from_excel: false,
    allow_social: false,
    show_benchmark: false,
    show_plaid: false,
    report_url: null,
    azure_sso_enabled: false,
    external_report: false,
    account_date_format: "MM/DD/YYYY",
    show_batch_id: false,
    allow_news: false,
    allow_account_invitation: false,
    allow_fees_calc: false,
    allow_deal_tab_open: false,
    allow_gpt: false,
    allow_effective_range: false,
    show_telescope: false,
    enable_crypto: false,
    html_template: null,
    allow_IBI_reports: false,
    hedge_fund: false,
    adjustable_columns: false,
    nullify_unfunded_commitment: false,
    batch_runtime_summary: false,
    show_meitav_management_fees: false,
    show_wide_view: false,
    show_hcs_view: false,
    enable_user_management: false,
    look_through_investments: false,
    disable_apply_filters: false,
    allow_use_date_range_switch: false,
    show_cashflow: false,
    show_debt: false,
    show_secondary_pricing: false,
    single_asset_report: false,
    show_correlations: false,
    support_investor_groups: false,
    allow_split_by_group: false,
    investor_level_characteristics: false,
    allow_ai_extraction: false,
    show_global_investor_filter: false,
    is_freemium: false,
    upgraded_from_freemium: false,
    freemium_account_settings: null,
    validation_panel: false,
    dataroom_phone_number: null,
    sanitize_documents: false,
    allow_extended_groups: false,
    show_smartsheet: false,
    show_ai_report_generator: false,
    show_ai_intelligence: false,
    enable_portfolio_construction: false,
    show_localization: false,
    welcome_video_url: null,
    allow_ai_deal_creation: false,
    show_recommendations: false,
    enable_bulk_upload_review: false
  },
  roles: [],
  audiences: {
    everyone: "",
    tenants_team: "",
    capital_seeker: "",
    guest: "",
    viewer: "",
    me: ""
  },
  bookmarks: [],
  managers: [],
  selectedBookmark: null,
  opportunity_id: 8,
  default_bookmarks: {
    deal: null,
    quick_view: null,
    report: [
      {
        report_type: "portfolio_analysis",
        params: null
      },
      { report_type: "comparison", params: null },
      {
        report_type: "deal_view",
        params: null
      },
      { report_type: "external", params: null }
    ],
    data_operations: null,
    investor_view: null,
    correlation_matrix: null
  },

  showUnlockFullAccess: false,
  dealsTableColumns: [],
  numberFormat: {
    abbreviated: false,
    decimal: 1
  },
  notifications: {
    list: [],
    page: 1,
    unreadItems: 0,
    filterByDeal: false,
    loading: false,
    hasMore: true
  },
  filesFilters: defaultFileFilters,
  workspaceColumns: [],
  workspaceFilters: {},
  fileRepoColumns: {},
  portfolioConstruct: null,
  amortizationTableColumns: {},
  digestSettings: {
    digestEmailEnabled: false,
    deliveryTime: "",
    digestContentSummary: "",
    useClientInbox: false
  }
};

export const reports: IReports = {
  meta: {
    assetClasses: [],
    measures: [],
    characteristics: [],
    measureClasses: [],
    dealFilters: {},
    comparisonColumns: [],
    calculatedFields: [],
    deals: [],
    KPIFields: [],
    KPICharacteristics: []
  },
  expandedRows: new Set(),
  queries: reportsQueries || staticReportsQueries,
  tableData: null,
  secondaryTableData: null,
  quick_view_meta: {
    measures: [],
    characteristics: [],
    dealFilters: {},
    calculatedFields: []
  },
  refetchReports: null,
  ignoreDefaultView: Boolean(reportsQueries),
  quickViewQueries: quickViewQueries,
  is_hedge_fund: false,
  correlationMatrixSettings: correlationMatrixSettings
};

export const dealModule: IDealModule = {
  deal: {
    id: undefined!,
    account_id: undefined!,
    account_logo: undefined!,
    deal_stage_id: undefined!,
    user_id: undefined!,
    asset_class_id: undefined!,
    sub_asset_class_id: undefined!,
    chart_settings: undefined!,
    type: undefined!,
    title: undefined!,
    asset_class: undefined!,
    confirm_change: undefined!,
    sub_asset_class: undefined!,
    deal_capital_seeker: undefined!,
    logo: null,
    crunchbase_data_id: null,
    vintage: null,
    currency: null,
    geography: null,
    crypto_coin_icon: null,
    parent: null,
    sub_assets: [],
    status: undefined!,
    email: undefined!,
    rounds: [],
    deal_role: null,
    nullify_unfunded_commitment: false,
    management_fee_setting: undefined,
    success_fee_setting: undefined,
    created_at: undefined!,
    updated_at: undefined!,
    deleted_at: undefined!,
    rating: null
  },
  uploadingProgress: [],
  fileMeta: {},
  folderMeta: {},
  newsMeta: {},
  emailMeta: null,
  noteMeta: {},
  noteLock: {
    editingBy: "",
    editingById: null!,
    noteId: null!
  },
  hierarchyPanelOpen: false,
  statuses: [],
  dealShareLoading: false,
  investors: [],
  measureParameters: [],
  queries: DealsStateFromQueries(),
  logoToSave: null,
  // ----------- new structure ------------
  folders: {},
  forms: {},
  files: {},
  emails: {},
  notes: {},

  characteristicsManage: null,

  reachOutManage: null,
  measures: [],
  measureDataTableFilters: defaultMeasureDataTableFilters,

  opportunities: [],
  shares: [],
  dealFilters: {},
  form: {
    loading: false,
    request_refresh: false,
    data: null
  },
  roundsWithInvestors: [],
  documentTypes: []
};

export const tagger: ITagger = {
  documentIds: [],
  documents: {},
  tags: [],
  analyzingStatus: "pending"
};

export const initialState: IStore = {
  userModule: userModule,
  dealModule: {
    ...dealModule,
    queries: { ...dealModule.queries, filters: {} }
  },

  comments: {
    general: {},
    specific: {},
    generalLoading: false,
    specificLoading: false,
    error: undefined,
    loading: false
  },

  view: viewStateFromQueries(),
  reports,
  taskManagment: {
    tasks: [],
    create: {
      isOpen: false
    },
    task: null,
    deals: []
  },
  dataRooms: {
    connections: [],
    connection: null,
    connectMessage: null
  },
  tagger,
  documents: [],
  bulkUpload: { batches: {} }
};

export const StoreContext = createContext<IStore>(initialState);
export const useStore = () => useContextSelector(StoreContext, (store: IStore) => store);
